/**
 * The list of every scenario (ADR-218, specs/changes/change-page/contract.md
 * §3.3, §3.4, §6): the feature files of GET /docs?type=spec_feature, flattened
 * into one row per scenario, and the filters the page applies in the browser.
 */
import type { DocIndex, FeatureFilePull } from '@/services/docsApi';
import type { Scenario, ScenarioState } from '@/lib/changes/model';

export interface ChangeRef {
  owner: string;
  repo: string;
  slug: string;
}

export interface ScenarioRow extends Scenario {
  /** Unique on the page: a scenario may have no id, and ids repeat across repositories. */
  key: string;
  repo: string;
  path: string;
  file: string;
  docId: string;
  runner: string;
  changes: ChangeRef[];
  /** The open pull request it is read from; null when it reads the default branch. */
  pullRequest: FeatureFilePull | null;
}

export interface ScenarioFilter {
  repo: string;
  state: string;
  q: string;
}

/** The states a row can be in, in the order a reader asks, and the filter's word for no tag. */
export const SCENARIO_STATES: { value: string; label: string }[] = [
  { value: 'proposed', label: 'Proposed' },
  { value: 'built', label: 'Built' },
  { value: 'defect', label: 'Defect' },
  { value: 'unbuilt', label: 'Unbuilt' },
  { value: 'open', label: 'Open' },
  { value: 'untagged', label: 'No state' },
];

export function stateKey(state: ScenarioState): string {
  return state || 'untagged';
}

export function scenarioRows(files: DocIndex[]): ScenarioRow[] {
  return files.flatMap((file) =>
    (file.scenarios ?? []).map((scenario, i) => ({
      ...scenario,
      key: `${file.repo_full_name}#${file.path}#${i}`,
      repo: file.repo_full_name,
      path: file.path,
      file: file.path.split('/').pop() ?? file.path,
      docId: file.id,
      runner: file.runner ?? '',
      changes: file.changes ?? [],
      pullRequest: file.pull_request ?? null,
    })),
  );
}

export function repositoriesOf(rows: ScenarioRow[]): string[] {
  return [...new Set(rows.map((row) => row.repo))].sort();
}

/** The filter in force: one repository, one state, and a word of the id or the title. */
export function filterRows(rows: ScenarioRow[], { repo, state, q }: ScenarioFilter): ScenarioRow[] {
  const words = q.trim().toLowerCase();
  return rows.filter((row) =>
    (!repo || row.repo === repo)
    && (!state || stateKey(row.state) === state)
    && (!words || row.id.toLowerCase().includes(words) || row.title.toLowerCase().includes(words)),
  );
}

export function countByState(rows: ScenarioRow[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const row of rows) counts[stateKey(row.state)] = (counts[stateKey(row.state)] ?? 0) + 1;
  return counts;
}
